/**
 * Setlist calculator utility
 *
 * Computes where the "Set End" divider should appear in a ranked song list,
 * and how much time remains in the current set.
 *
 * All durations are in SECONDS unless otherwise noted.
 */

/**
 * Count how many songs fit in a remaining time budget.
 *
 * Takes remainingSec as a parameter — no Date.now() inside — so callers can
 * snapshot the clock once and keep the result stable.
 *
 * @param {Object} options
 * @param {number} options.remainingSec
 * @param {Array}  options.rankedSongs
 * @param {Object|null} options.upNextSong
 * @param {number} options.pauseTimeSec
 * @param {number} options.defaultSongTimeSec
 * @param {Object} options.songLengthMap
 * @param {Object|null} options.currentSong – song already playing; its remainder is spent before slots are counted
 * @param {string|number|Date|null} options.currentSongStartedAt – when currentSong began (falls back to currentSong.performedAt)
 * @param {number} options.nowMs – clock for the current-song remainder; defaults to Date.now()
 * @returns {{ slots: number, status: 'inactive'|'expired'|'partial'|'all-fit' }}
 */
export function countFittingSongs({
  remainingSec,
  rankedSongs = [],
  upNextSong = null,
  pauseTimeSec = 30,
  defaultSongTimeSec = 240,
  songLengthMap = {},
  currentSong = null,
  currentSongStartedAt = null,
  nowMs = Date.now()
} = {}) {
  if (remainingSec == null || Number.isNaN(remainingSec)) {
    return { slots: 0, status: "inactive" };
  }
  if (remainingSec <= 0) {
    return { slots: 0, status: "expired" };
  }

  const getSongDuration = (song) => {
    if (!song) return defaultSongTimeSec;
    const id = song.id || song.songId;
    const len = song.songLength || songLengthMap[id] || null;
    return len && len > 0 ? len : defaultSongTimeSec;
  };

  let remaining = remainingSec;

  if (currentSong) {
    const startedAt = currentSongStartedAt || currentSong.performedAt || null;
    let elapsed = 0;
    if (startedAt) {
      const startedMs = new Date(startedAt).getTime();
      if (!Number.isNaN(startedMs)) {
        elapsed = Math.max(0, (nowMs - startedMs) / 1000);
      }
    }
    const remainingSong = Math.max(0, getSongDuration(currentSong) - elapsed);
    remaining -= remainingSong + pauseTimeSec;
    if (remaining <= 0) {
      return { slots: 0, status: "partial" };
    }
  }

  let slots = 0;

  if (upNextSong) {
    remaining -= getSongDuration(upNextSong) + pauseTimeSec;
    if (remaining <= 0) {
      return { slots: 1, status: "partial" };
    }
    slots = 1;
  }

  for (let i = 0; i < rankedSongs.length; i++) {
    remaining -= getSongDuration(rankedSongs[i]) + pauseTimeSec;
    if (remaining <= 0) {
      // Song at i exhausted the budget; it is the first that does not fit.
      // slots already counts songs that fully fit (upNext + ranked 0..i-1).
      return { slots, status: "partial" };
    }
    slots += 1;
  }

  return { slots, status: "all-fit" };
}

/**
 * Resolve a persisted set-end song count into a render index for the ranked list.
 *
 * @param {Object} options
 * @param {number|null|undefined} options.setEndSongCount
 * @param {Object|null} options.upNextSong
 * @param {Array} options.rankedSongs
 * @param {number} options.fallbackIndex – live computeSetEndIndex result when count is null
 * @returns {number}
 */
export function resolveSetEndIndex({
  setEndSongCount,
  upNextSong = null,
  rankedSongs = [],
  fallbackIndex = -1
} = {}) {
  if (setEndSongCount == null) return fallbackIndex;
  const offset = upNextSong ? 1 : 0;
  const raw = setEndSongCount - offset;
  const max = rankedSongs.length;
  if (raw < 0) return 0;
  if (raw > max) return max;
  return raw;
}

/**
 * Compute the index in the ranked song list after which the set time is exhausted.
 *
 * The "up next" song (if any) is counted first (it WILL be played), then songs
 * from the ranked list are accumulated until the budget runs out.
 *
 * Thin clock-reading wrapper over countFittingSongs. Preserves the historical
 * return contract (including the -1 sentinel) as the fallback when
 * setEndSongCount is null.
 *
 * @param {Array}  rankedSongs       – sorted results array (excluding upNext & performed)
 * @param {Object} options
 * @param {number} options.setDurationMinutes   – duration of the current set in minutes
 * @param {number} options.pauseTimeSec         – seconds of pause between songs
 * @param {number} options.defaultSongTimeSec   – fallback song length in seconds
 * @param {Object|null} options.upNextSong      – the locked "up next" song (or null)
 * @param {Object|null} options.currentSong     – the currently playing song (or null)
 * @param {string|null} options.setStartedAt    – ISO timestamp when the set started
 * @param {Object}      options.songLengthMap   – { songId: lengthInSeconds } lookup
 * @returns {number} index in rankedSongs after which the set ends (-1 if all fit, or no set active)
 */
export function computeSetEndIndex(rankedSongs, options = {}) {
  const {
    setDurationMinutes = 0,
    pauseTimeSec = 30,
    defaultSongTimeSec = 240,
    upNextSong = null,
    currentSong = null,
    setStartedAt = null,
    songLengthMap = {}
  } = options;

  if (!setDurationMinutes || setDurationMinutes <= 0 || !setStartedAt) return -1;

  const totalBudgetSec = setDurationMinutes * 60;
  const now = Date.now();
  const elapsed = (now - new Date(setStartedAt).getTime()) / 1000;
  const remainingSec = totalBudgetSec - elapsed;

  const { slots, status } = countFittingSongs({
    remainingSec,
    rankedSongs,
    upNextSong,
    pauseTimeSec,
    defaultSongTimeSec,
    songLengthMap,
    currentSong,
    currentSongStartedAt: currentSong?.performedAt ?? null
  });

  if (status === "inactive" || status === "expired" || status === "all-fit") {
    return -1;
  }

  // Map slot count back to the historical divider index.
  return resolveSetEndIndex({
    setEndSongCount: slots,
    upNextSong,
    rankedSongs,
    fallbackIndex: -1
  });
}

/**
 * Compute remaining seconds in the current set.
 *
 * @param {number} setDurationMinutes
 * @param {string|null} setStartedAt – ISO timestamp
 * @returns {number} seconds remaining (can be negative if over)
 */
export function computeRemainingSetTime(setDurationMinutes, setStartedAt) {
  if (!setDurationMinutes || !setStartedAt) return -1;
  const totalSec = setDurationMinutes * 60;
  const elapsed = (Date.now() - new Date(setStartedAt).getTime()) / 1000;
  return Math.round(totalSec - elapsed);
}

/**
 * True when a set clock is still running after its current song was ended.
 * That is an early Set end that was saved only on the song: a refresh must
 * close the set instead of resuming the leftover countdown.
 */
export function setLeftOpenAfterSongEnded({
  setlistEnabled = false,
  setStartedAt = null,
  setDurationMinutes = 0,
  hasCurrentSong = false,
  lastSongEnded = false,
  setEndSongCount = null,
  nowMs = Date.now()
} = {}) {
  if (!setlistEnabled || !setStartedAt || hasCurrentSong || !lastSongEnded) return false;
  if (!setDurationMinutes) return false;
  const elapsed = (nowMs - new Date(setStartedAt).getTime()) / 1000;
  const remaining = Math.round(setDurationMinutes * 60 - elapsed);
  if (remaining > 0) return true;
  // The countdown already reads zero, but the divider is still pinned below the top.
  return setEndSongCount != null && setEndSongCount > 0;
}

/**
 * Format seconds into MM:SS string.
 */
export function formatCountdown(totalSeconds) {
  if (totalSeconds < 0) return "0:00";
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Build a songLength lookup map from vote results or songs array.
 * @param {Array} songs – array of song objects with .id and .songLength
 * @returns {Object} { songId: lengthInSeconds }
 */
export function buildSongLengthMap(songs = []) {
  const map = {};
  songs.forEach((s) => {
    const id = s.id || s.songId || s.song?.id;
    const len = s.songLength || s.song?.songLength || null;
    if (id && len) map[id] = len;
  });
  return map;
}
